import {
  ToolAccessMode as ToolAccessModeEnum,
  ReasoningEffort as ReasoningEffortEnum,
  ServiceTier as ServiceTierEnum,
} from '@erikdakoda/database/enums';
import { aiToolDateTimeSchema } from '@erikdakoda/ai-tool/aiToolDateTimeSchema';
import { MODEL_USE_CASES } from '@erikdakoda/ai-model/shared/modelPolicy';
import { z } from 'zod';
import { promptForAiToolsBaseSchema } from './PromptForAiTools';

/** Scalar / leaf fields for a bot (no nested childPrompts). */
export const botForAiToolsBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  createdAt: aiToolDateTimeSchema,
  updatedAt: aiToolDateTimeSchema,
  publishedAt: aiToolDateTimeSchema.nullable().optional(),
  private: z.boolean(),
  ownerId: z.string(),
  spaceId: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  avatarId: z.string().nullable().optional(),
  useCase: z
    .enum(MODEL_USE_CASES)
    .nullable()
    .describe('Work this bot does. When set, the catalog picks fallback models for the use case.'),
  integration: z.string().nullable(),
  modelName: z
    .string()
    .nullable()
    .describe('Catalog model. Null with a use case means Auto: the catalog picks the model.'),
  serviceTier: z.enum(ServiceTierEnum),
  maxSteps: z
    .number()
    .int()
    .positive()
    .optional()
    .describe('Max tool-use steps the bot may take. Defaults to 10 when omitted.'),
  temperature: z.number(),
  reasoningEffort: z
    .enum(ReasoningEffortEnum)
    .describe(
      'Reasoning effort for the bot model. Ignored while the model is Auto: the effort then comes from the use-case model and effort pair.',
    ),
  verbosity: z.enum(['low', 'medium', 'high']),
  toolAccessMode: z
    .enum(ToolAccessModeEnum)
    .describe(
      'Maximum write access this bot grants to tools (native, MCP, and connected). Limitation only.',
    ),
  /** Matches `JsonValue` (object, array, scalar, or null). */
  systemPromptValues: z.unknown().nullable().optional(),
  outputSchema: z
    .string()
    .nullable()
    .optional()
    .describe('Analysis/output schema registry id (e.g. `uvilo-ai.analyze-coach-convo@1`).'),
  autoStart: z.boolean(),
  analysisBotGroup: z.string().nullable().optional().describe('Analyzer bot group (e.g. `agent`).'),
  analysisBotHandle: z
    .string()
    .nullable()
    .optional()
    .describe('Analyzer bot handle (e.g. `analyze-coach-convo`).'),
  analysisSchema: z
    .string()
    .nullable()
    .optional()
    .describe("Schema id stamped onto Convo.analysis when this bot's convos are analyzed."),
  toolSetId: z
    .string()
    .optional()
    .describe(
      'ToolSet id that gates which tools this bot may use. Defaults to No Tools (tls_notools).',
    ),
  group: z.string().nullable().optional(),
  handle: z.string().nullable().optional(),
});

/** One row under `bot.childPrompts` (join + nested prompt); the ORM may include extra scalar fields. */
export const childPromptSchema = z.looseObject({
  id: z.string(),
  ord: z.number().nullable().optional(),
  values: z.unknown().nullable().optional(),
  prompt: promptForAiToolsBaseSchema,
});

/**
 * Canonical Zod shape for a Bot in AI tools (`id` included).
 * Nested `childPrompts` matches read/edit payloads from the ORM.
 */
export const BotForAiToolsSchema = botForAiToolsBaseSchema.extend({
  childPrompts: z.array(childPromptSchema),
});

export type BotForAiTools = z.infer<typeof BotForAiToolsSchema>;

export type BotChildLinkForAiTools = NonNullable<BotForAiTools['childPrompts']>[number];
